import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Alert,
  useTheme
} from '@mui/material';
import {
  TrendingUp as TrendingIcon,
  School as SchoolIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CompletedIcon,
  PlayArrow as PlayIcon,
  Star as StarIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { 
  LearningAnalytics, 
  LearningPath, 
  MindMapNode, 
  Achievement
} from '../../types/learning';

interface LearningDashboardProps {
  analytics: LearningAnalytics;
  learningPath: LearningPath;
  nodes: MindMapNode[];
  achievements: Achievement[];
  onNodeClick: (node: MindMapNode) => void;
  onStartAssessment: () => void;
}

const LearningDashboard: React.FC<LearningDashboardProps> = ({
  analytics,
  learningPath,
  nodes,
  achievements,
  onNodeClick,
  onStartAssessment
}) => {
  const theme = useTheme();
  const [nextRecommended, setNextRecommended] = useState<MindMapNode[]>([]);

  useEffect(() => {
    // Find next recommended nodes
    const next = nodes.filter(node => {
      const progress = learningPath.progress[node.id] || 0;
      const isUnlocked = !node.prerequisites.some(prereq => 
        (learningPath.progress[prereq] || 0) < 0.5
      );
      return progress > 0 && progress < 0.8 && isUnlocked;
    }).slice(0, 3);
    setNextRecommended(next);
  }, [nodes, learningPath.progress]);

  const getLearningStyleIcon = (style: string) => {
    const icons = {
      visual: '👁️',
      auditory: '👂',
      reading: '📖',
      kinesthetic: '✋',
      sequential: '📊',
      active: '⚡',
      sensing: '🔬',
      verbal: '💬'
    };
    return icons[style as keyof typeof icons] || '🧠';
  };

  const getLearningStyleColor = (style: string) => {
    const colors = {
      visual: theme.palette.primary.main,
      auditory: theme.palette.secondary.main,
      reading: theme.palette.success.main,
      kinesthetic: theme.palette.warning.main,
      sequential: theme.palette.info.main,
      active: theme.palette.error.main,
      sensing: theme.palette.primary.main,
      verbal: theme.palette.secondary.main
    };
    return colors[style as keyof typeof colors] || theme.palette.grey[500];
  };

  const getAchievementIcon = (achievement: Achievement) => {
    return achievement.icon;
  };

  const getAchievementColor = (rarity: string) => {
    const colors = {
      common: theme.palette.grey[500],
      uncommon: theme.palette.success.main,
      rare: theme.palette.info.main,
      epic: theme.palette.warning.main,
      legendary: theme.palette.error.main
    };
    return colors[rarity as keyof typeof colors] || theme.palette.grey[500];
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 0.8) return theme.palette.success.main;
    if (progress >= 0.5) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon />
          Learning Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your progress and discover your next learning steps
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Overview Stats */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingIcon />
                Learning Overview
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" color="primary.main">
                      {analytics.nodesCompleted}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nodes Completed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" color="secondary.main">
                      {formatTime(analytics.totalTimeSpent)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Time Spent
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" color="success.main">
                      {Math.round(analytics.averageMasteryLevel * 100)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average Mastery
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" color="warning.main">
                      {analytics.learningVelocity.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nodes/Hour
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Overall Progress */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Overall Progress: {analytics.nodesCompleted}/{analytics.totalNodes} nodes
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(analytics.nodesCompleted / analytics.totalNodes) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Learning Style Profile */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PsychologyIcon />
                Your Learning Style
              </Typography>
              
              <Grid container spacing={2}>
                {Object.entries(analytics.learningStyleProfile).map(([style, value]) => (
                  <Grid item xs={6} sm={4} md={3} key={style}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Avatar sx={{ 
                        bgcolor: getLearningStyleColor(style), 
                        width: 40, 
                        height: 40, 
                        mx: 'auto', 
                        mb: 1 
                      }}>
                        {getLearningStyleIcon(style)}
                      </Avatar>
                      <Typography variant="caption" sx={{ textTransform: 'capitalize', display: 'block' }}>
                        {style.replace(/([A-Z])/g, ' $1').trim()}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={value * 100} 
                        sx={{ mt: 0.5 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(value * 100)}%
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PlayIcon />
                Continue Learning
              </Typography>
              
              {nextRecommended.length > 0 ? (
                <List>
                  {nextRecommended.map((node, index) => (
                    <React.Fragment key={node.id}>
                      <ListItem 
                        button 
                        onClick={() => onNodeClick(node)}
                        sx={{ borderRadius: 1, mb: 1 }}
                      >
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: getProgressColor(learningPath.progress[node.id] || 0) }}>
                            {learningPath.progress[node.id] >= 0.8 ? <CompletedIcon /> : <PlayIcon />}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={node.title}
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {node.type} • Level {node.difficultyLevel} • {node.estimatedTimeMinutes}min
                              </Typography>
                              <LinearProgress 
                                variant="determinate" 
                                value={(learningPath.progress[node.id] || 0) * 100}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < nextRecommended.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  Great job! You've completed all available learning nodes. Check back for new content!
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Achievements */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrophyIcon />
                Achievements
              </Typography>
              
              <List dense>
                {achievements.map((achievement) => {
                  const isEarned = learningPath.achievements.includes(achievement.id);
                  return (
                    <ListItem key={achievement.id} sx={{ opacity: isEarned ? 1 : 0.5 }}>
                      <ListItemIcon>
                        <Avatar sx={{ 
                          bgcolor: isEarned ? getAchievementColor(achievement.rarity) : theme.palette.grey[300],
                          width: 32,
                          height: 32
                        }}>
                          {getAchievementIcon(achievement)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={achievement.title}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {achievement.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Chip 
                                label={achievement.rarity} 
                                size="small" 
                                color={isEarned ? 'primary' : 'default'}
                                variant="outlined"
                              />
                              <Chip 
                                label={`${achievement.points} pts`} 
                                size="small" 
                                color="secondary"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>

          {/* Learning Insights */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InsightsIcon />
                Learning Insights
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Your Strengths
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {analytics.strengths.map((strength, index) => (
                    <Chip key={index} label={strength} size="small" color="success" />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Areas to Focus On
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {analytics.weaknesses.map((weakness, index) => (
                    <Chip key={index} label={weakness} size="small" color="warning" />
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Recommended Focus
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {analytics.recommendedFocusAreas.map((area, index) => (
                    <Chip key={index} label={area} size="small" color="primary" />
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Quick Actions
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={onStartAssessment}
                  startIcon={<PsychologyIcon />}
                >
                  Retake Assessment
                </Button>
                
                <Button 
                  variant="outlined" 
                  fullWidth
                  startIcon={<TimerIcon />}
                >
                  Study Session
                </Button>
                
                <Button 
                  variant="outlined" 
                  fullWidth
                  startIcon={<StarIcon />}
                >
                  View All Achievements
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LearningDashboard;
